import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Menu, Card, Statistic, Table, Row, Col,
  Typography, Space, Avatar, Progress, Button, Divider, Tag, Badge
} from 'antd';
import {
  DollarOutlined, ShoppingOutlined, ToolOutlined,
  TransactionOutlined, HistoryOutlined, MenuFoldOutlined,
  MenuUnfoldOutlined, RiseOutlined, FallOutlined
} from '@ant-design/icons';
import { Bar, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { getUserData } from '../../../hooks/Auth';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const SupplierDashboardd = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const user = getUserData();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('https://techcrew.pk/ulc/Get_SupplierDashboardStats.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: user?.user?.id || 0 })
        });
        const data = await response.json();
        setStats(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!stats) {
    return <div>Error loading dashboard data</div>;
  }

  // Chart data preparation
  const earningsTrendData = {
    labels: stats.monthly_trend.map(item => item.month),
    datasets: [
      {
        label: 'Monthly Earnings (SAR)',
        data: stats.monthly_trend.map(item => parseFloat(item.amount)),
        backgroundColor: '#52c41a',
        borderColor: '#389e0d',
        borderWidth: 1
      }
    ]
  };

  const equipmentDistributionData = {
    labels: stats.equipment_categories.map(item => item.category_name),
    datasets: [
      {
        label: 'Equipment by Category',
        data: stats.equipment_categories.map(item => parseInt(item.count)),
        backgroundColor: [
          '#1890ff',
          '#722ed1',
          '#13c2c2',
          '#52c41a',
          '#faad14'
        ],
        borderWidth: 0
      }
    ]
  };

  // Table columns
  const transactionColumns = [
    {
      title: 'Date & Time',
      dataIndex: 'transaction_date',
      key: 'date',
      width: 180,
      render: date => new Date(date).toLocaleString()
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'type',
      width: 120,
      render: type => (
        <Tag color={type === 'receive' ? 'green' : 'orange'} style={{ margin: 0 }}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Amount (SAR)',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: amount => (
        <Text strong style={{ color: amount.startsWith('-') ? '#ff4d4f' : '#52c41a' }}>
          {amount}
        </Text>
      )
    },
    {
      title: 'Description',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    }
  ];

  const equipmentColumns = [
    {
      title: 'Equipment',
      dataIndex: 'equipment_name',
      key: 'equipment'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: price => `SAR ${price}`
    },
    {
      title: 'Hourly Rate',
      dataIndex: 'hourprice',
      key: 'hourprice',
      render: price => `SAR ${price}/hr`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Badge 
          status={status === 'Active' ? 'success' : 'error'} 
          text={status} 
        />
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light">
        <div className="logo" style={{ height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {collapsed ? (
            <Title level={4} style={{ margin: 0 }}>ULC</Title>
          ) : (
            <Title level={3} style={{ margin: 0 }}>Supplier Portal</Title>
          )}
        </div>
        <Menu theme="light" mode="inline" defaultSelectedKeys={['1']}>
          <Menu.Item key="1" icon={<DollarOutlined />}>Dashboard</Menu.Item>
          <Menu.Item key="2" icon={<ShoppingOutlined />}>My Orders</Menu.Item>
          <Menu.Item key="3" icon={<ToolOutlined />}>My Equipment</Menu.Item>
          <Menu.Item key="4" icon={<TransactionOutlined />}>Transactions</Menu.Item>
          <Menu.Item key="5" icon={<HistoryOutlined />}>History</Menu.Item>
        </Menu>
      </Sider>

      <Layout className="site-layout">
        {/* Header */}
        <Header style={{ padding: 0, background: '#fff', boxShadow: '0 1px 4px rgba(0,21,41,0.08)' }}>
          <Row justify="space-between" align="middle" style={{ padding: '0 24px' }}>
            <Col>
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
                style: { fontSize: '18px' }
              })}
            </Col>
            <Col>
              <Space>
                <Avatar style={{ backgroundColor: '#52c41a' }}>
                  {user.name ? user.name.charAt(0).toUpperCase() : 'S'}
                </Avatar>
                <Text strong>{user.name || 'Supplier'}</Text>
              </Space>
            </Col>
          </Row>
        </Header>

        {/* Main Content */}
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Total Earnings"
                  value={stats.total_earnings}
                  prefix="SAR"
                  valueStyle={{ color: '#52c41a' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#52c41a"
                  trailColor="#f0f0f0"
                />
                <Divider style={{ margin: '8px 0' }} />
                <Space>
                  <RiseOutlined style={{ color: '#52c41a' }} />
                  <Text type="secondary">Last 30 days</Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Total Withdrawals"
                  value={stats.total_withdrawals}
                  prefix="SAR"
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#ff4d4f"
                  trailColor="#f0f0f0"
                />
                <Divider style={{ margin: '8px 0' }} />
                <Space>
                  <FallOutlined style={{ color: '#ff4d4f' }} />
                  <Text type="secondary">Last 30 days</Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Available Balance"
                  value={stats.available_balance}
                  prefix="SAR"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Progress
                  percent={100}
                  showInfo={false}
                  strokeColor="#1890ff"
                  trailColor="#f0f0f0"
                />
                <Divider style={{ margin: '8px 0' }} />
                <Button type="primary" size="small">Withdraw</Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bordered={false} hoverable>
                <Statistic
                  title="Active Equipment"
                  value={stats.active_equipment}
                  suffix="items"
                />
                <Progress
                  percent={(stats.active_equipment / stats.total_equipment) * 100}
                  showInfo={false}
                  strokeColor="#722ed1"
                  trailColor="#f0f0f0"
                />
                <Divider style={{ margin: '8px 0' }} />
                <Button type="dashed" size="small">Add Equipment</Button>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card 
                title="Earnings Trend" 
                bordered={false}
                hoverable
              >
                <Line 
                  data={earningsTrendData} 
                  options={{ 
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `SAR ${context.raw}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            return `SAR ${value}`;
                          }
                        }
                      }
                    }
                  }} 
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card 
                title="Equipment Distribution" 
                bordered={false}
                hoverable
              >
                <Bar 
                  data={equipmentDistributionData} 
                  options={{ 
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `${context.raw} items`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    }
                  }} 
                />
              </Card>
            </Col>
          </Row>

          {/* Tables Row */}
         <Row gutter={[16, 16]}>
  <Col xs={24} xl={24} md={24}>
    <Card
      title="Recent Transactions"
      bordered={false}
      extra={<Button type="primary" ghost>View All</Button>}
      hoverable
    >
      <Table
        columns={transactionColumns}
        dataSource={stats.recent_transactions}
        rowKey="transaction_id"
        pagination={{ pageSize: 5 }}
        size="middle"
        scroll={{ x: '100%' }}
      />
    </Card>
  </Col>
</Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SupplierDashboardd;